import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ChatBubbleRoundedIcon from "@mui/icons-material/ChatBubbleRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Skeleton,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";

const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      }).format(new Date(value))
    : "—";

const BenefitCard = ({
  icon,
  label,
  used = 0,
  limit,
  unlimited,
  remaining,
}) => {
  const hasUnlimitedAccess = unlimited || limit >= 9999;
  const progress =
    hasUnlimitedAccess || !limit ? 0 : Math.min((used / limit) * 100, 100);
  const available = hasUnlimitedAccess
    ? "Unlimited"
    : (remaining ?? Math.max((limit || 0) - used, 0));

  return (
    <Box
      sx={{
        p: 1.75,
        minWidth: 0,
        border: "1px solid rgba(255,255,255,.12)",
        borderRadius: 2.5,
        bgcolor: "rgba(255,255,255,.07)",
        backdropFilter: "blur(8px)",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            display: "grid",
            placeItems: "center",
            borderRadius: 1.75,
            color: "#ffad5c",
            bgcolor: "rgba(255,128,0,.15)",
          }}
        >
          {icon}
        </Box>
        <Typography fontSize={11} sx={{ color: "rgba(255,255,255,.62)" }}>
          {hasUnlimitedAccess ? "No limit" : `${used} used`}
        </Typography>
      </Box>
      <Typography
        fontSize={12}
        fontWeight={650}
        sx={{ color: "white", mt: 1.25 }}
      >
        {label}
      </Typography>
      <Typography
        variant="h6"
        fontWeight={800}
        sx={{ color: "white", mt: 0.2 }}
      >
        {available}
        {!hasUnlimitedAccess && (
          <Typography
            component="span"
            fontSize={11}
            sx={{ color: "rgba(255,255,255,.62)", ml: 0.6 }}
          >
            remaining
          </Typography>
        )}
      </Typography>
      {!hasUnlimitedAccess && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            mt: 1.1,
            height: 5,
            borderRadius: 4,
            bgcolor: "rgba(255,255,255,.12)",
            "& .MuiLinearProgress-bar": { bgcolor: "primary.main" },
          }}
        />
      )}
    </Box>
  );
};

const MyMembershipPanel = ({ membership, history, loading, error }) => {
  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        height={290}
        sx={{ borderRadius: 3, mb: 3 }}
      />
    );
  }
  if (error)
    return (
      <Alert severity="warning" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  if (!membership || membership.active === false) return null;

  const benefits = membership.benefits || {};
  const startedAt = new Date(membership.starts_at).getTime();
  const endsAt = new Date(membership.ends_at).getTime();
  const calculatedDays = Math.ceil((endsAt - startedAt) / 86400000);
  const totalDays = Number.isFinite(calculatedDays)
    ? Math.max(calculatedDays, 1)
    : 1;
  const elapsedPercent = Math.min(
    Math.max(
      ((totalDays - Number(membership.days_remaining || 0)) / totalDays) * 100,
      0,
    ),
    100,
  );

  return (
    <Box sx={{ mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          position: "relative",
          overflow: "hidden",
          p: { xs: 2.25, md: 3.25 },
          borderRadius: 3.5,
          color: "white",
          background:
            "linear-gradient(135deg, #121b2d 0%, #243a5a 68%, #314d70 100%)",
          boxShadow: "0 20px 55px rgba(18,27,45,.22)",
          "&::after": {
            content: '""',
            position: "absolute",
            width: 260,
            height: 260,
            right: -100,
            top: -150,
            borderRadius: "50%",
            bgcolor: "rgba(255,128,0,.13)",
          },
        }}
      >
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2.5,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", gap: 1.75, alignItems: "flex-start" }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                  borderRadius: 2.25,
                  color: "white",
                  bgcolor: "primary.main",
                  boxShadow: "0 10px 25px rgba(255,128,0,.25)",
                }}
              >
                <WorkspacePremiumRoundedIcon />
              </Box>
              <Box>
                <Chip
                  icon={<CheckCircleRoundedIcon />}
                  label="ACTIVE"
                  color="success"
                  size="small"
                  sx={{
                    height: 24,
                    fontWeight: 800,
                    letterSpacing: 0.5,
                    mb: 1,
                  }}
                />
                <Typography variant="h5" fontWeight={850} letterSpacing={-0.4}>
                  {membership.membership_name} membership
                </Typography>
                <Typography sx={{ mt: 0.35, color: "rgba(255,255,255,.7)" }}>
                  {membership.duration_label} plan · {membership.days_remaining}{" "}
                  days remaining
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                minWidth: { sm: 210 },
                p: 1.5,
                display: "flex",
                gap: 1.25,
                alignItems: "center",
                borderRadius: 2.25,
                bgcolor: "rgba(255,255,255,.07)",
                border: "1px solid rgba(255,255,255,.1)",
              }}
            >
              <CalendarMonthRoundedIcon sx={{ color: "#ffad5c" }} />
              <Box>
                <Typography
                  fontSize={10}
                  fontWeight={700}
                  letterSpacing={0.8}
                  sx={{ color: "rgba(255,255,255,.58)" }}
                >
                  VALID UNTIL
                </Typography>
                <Typography fontWeight={750}>
                  {formatDate(membership.ends_at)}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 0.8 }}
            >
              <Typography fontSize={11} sx={{ color: "rgba(255,255,255,.6)" }}>
                {formatDate(membership.starts_at)}
              </Typography>
              <Typography fontSize={11} sx={{ color: "rgba(255,255,255,.6)" }}>
                {formatDate(membership.ends_at)}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={elapsedPercent}
              sx={{
                height: 7,
                borderRadius: 5,
                bgcolor: "rgba(255,255,255,.12)",
                "& .MuiLinearProgress-bar": {
                  bgcolor: "primary.main",
                  borderRadius: 5,
                },
              }}
            />
          </Box>

          <Divider sx={{ my: 2.5, borderColor: "rgba(255,255,255,.11)" }} />
          <Typography
            fontSize={12}
            fontWeight={750}
            sx={{ color: "rgba(255,255,255,.72)", mb: 1.25 }}
          >
            YOUR BENEFITS
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
              gap: 1.5,
            }}
          >
            <BenefitCard
              icon={<PhoneRoundedIcon fontSize="small" />}
              label="Verified mobile numbers"
              used={benefits.verified_mobile_used}
              limit={benefits.verified_mobile_limit}
              remaining={benefits.verified_mobile_remaining}
            />
            <BenefitCard
              icon={<VisibilityRoundedIcon fontSize="small" />}
              label="Profile views"
              used={benefits.profile_view_used}
              limit={benefits.profile_view_limit}
              remaining={benefits.profile_view_remaining}
            />
            <BenefitCard
              icon={<ChatBubbleRoundedIcon fontSize="small" />}
              label="Messages"
              used={benefits.message_count_used}
              limit={benefits.message_limit}
              unlimited={benefits.is_unlimited_messages}
            />
          </Box>
        </Box>
      </Paper>

      <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
        <Button
          component={Link}
          to="/auth/history"
          variant="outlined"
          startIcon={<ReceiptLongRoundedIcon />}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
        >
          Payment history
        </Button>
      </Box>

      {!!history?.length && (
        <Accordion
          elevation={0}
          disableGutters
          sx={{
            mt: 1.5,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "12px !important",
            "&::before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreRoundedIcon />}
            sx={{ px: 2.25, minHeight: 60 }}
          >
            <HistoryRoundedIcon color="primary" sx={{ mr: 1.25 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography fontWeight={750}>Membership history</Typography>
              <Typography fontSize={11} color="text.secondary">
                View previous membership periods
              </Typography>
            </Box>
            <Chip
              label={history.length}
              size="small"
              sx={{ mr: 1, fontWeight: 700 }}
            />
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.25, pt: 0, pb: 1 }}>
            {history.map((item, index) => (
              <Box key={item.id}>
                {index > 0 && <Divider />}
                <Box
                  sx={{
                    py: 1.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1.25, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 9,
                        height: 9,
                        mt: 0.7,
                        flexShrink: 0,
                        borderRadius: "50%",
                        bgcolor:
                          item.status === "ACTIVE"
                            ? "success.main"
                            : "text.disabled",
                      }}
                    />
                    <Box>
                      <Typography fontSize={13} fontWeight={700}>
                        Membership #{item.id}
                      </Typography>
                      <Typography fontSize={12} color="text.secondary">
                        {formatDate(item.starts_at)} –{" "}
                        {formatDate(item.ends_at)}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={item.status}
                    color={item.status === "ACTIVE" ? "success" : "default"}
                    size="small"
                  />
                </Box>
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default MyMembershipPanel;
